import { Alert, Modal } from "@hakistudio/hakiui";
import {
  TASK_PRIORITIES, TASK_PRIORITY_LABELS, TASK_STATUSES, TASK_STATUS_LABELS,
  type TaskPriority, type TaskStatus,
} from "@sebas/core";
import { format } from "date-fns";
import { Trash2, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api";
import { useNotes } from "@/lib/queries";
import { useCreateTask, useDeleteTask, useLinkTaskNotes, useUpdateTask, type Task } from "@/lib/tasks";
import { Input, Select } from "@/components/ui/Field";

/** <input type="datetime-local"> wants local wall-clock, not an ISO instant. */
const toLocalInput = (iso: string) => format(new Date(iso), "yyyy-MM-dd'T'HH:mm");

export function TaskDialog({
  task,
  defaultStatus = "todo",
  onClose,
}: {
  task: Task | null;
  defaultStatus?: TaskStatus;
  onClose: () => void;
}) {
  const editing = Boolean(task);
  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? defaultStatus);
  const [priority, setPriority] = useState<TaskPriority>(task?.priority ?? "medium");
  const [dueAt, setDueAt] = useState(task?.dueAt ? toLocalInput(task.dueAt) : "");
  const [error, setError] = useState<string | null>(null);

  const create = useCreateTask();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const linkNotes = useLinkTaskNotes(task?.id ?? "");
  const { data: noteData } = useNotes({});

  const linkedIds = new Set((task?.notes ?? []).map((n) => n.id));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("Give the task a title.");
      return;
    }

    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      status,
      priority,
      // A local datetime becomes the UTC instant we store.
      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
    };
    const opts = {
      onSuccess: onClose,
      onError: (e: Error) => setError(e instanceof ApiError ? e.message : String(e)),
    };
    if (task) update.mutate({ id: task.id, ...payload }, opts);
    else create.mutate(payload, opts);
  };

  const toggleNote = (noteId: string) => {
    if (!task) return;
    const next = linkedIds.has(noteId)
      ? [...linkedIds].filter((id) => id !== noteId)
      : [...linkedIds, noteId];
    linkNotes.mutate(next);
  };

  return (
    <Modal isOpen onClose={onClose} title={editing ? "Edit task" : "New task"}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          autoFocus
          placeholder="What needs doing?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <textarea
          placeholder="Notes for yourself (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full resize-y rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:border-ink-faint focus:outline-none"
        />

        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Status"
            value={status}
            onChange={(v) => setStatus(v as TaskStatus)}
            options={TASK_STATUSES.map((s) => ({ label: TASK_STATUS_LABELS[s], value: s }))}
          />
          <Select
            label="Priority"
            value={priority}
            onChange={(v) => setPriority(v as TaskPriority)}
            options={TASK_PRIORITIES.map((p) => ({ label: TASK_PRIORITY_LABELS[p], value: p }))}
          />
        </div>

        <Input
          label="Due date"
          type="datetime-local"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          endContent={
            dueAt ? (
              <button type="button" aria-label="Clear due date" onClick={() => setDueAt("")}>
                <X className="h-3.5 w-3.5 text-ink-faint" />
              </button>
            ) : undefined
          }
        />

        {editing ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-ink-faint">Linked notes</span>
            {(noteData?.notes ?? []).length === 0 ? (
              <p className="text-sm text-ink-faint">No notes yet to link.</p>
            ) : (
              <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
                {(noteData?.notes ?? []).map((n) => (
                  <label
                    key={n.id}
                    className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 text-sm hover:bg-sunken"
                  >
                    <input
                      type="checkbox"
                      checked={linkedIds.has(n.id)}
                      onChange={() => toggleNote(n.id)}
                      className="h-4 w-4 accent-[var(--ui-primary)]"
                    />
                    <span className="truncate text-ink">{n.title || "Untitled"}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-ink-faint">Save the task first to link notes to it.</p>
        )}

        {error ? <Alert variant="danger" title={error} /> : null}

        <div className="mt-1 flex items-center gap-2">
          <Button type="submit" className="flex-1" disabled={create.isPending || update.isPending}>
            {editing ? "Save" : "Create task"}
          </Button>
          {editing ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label="Delete task"
              onClick={() => {
                if (confirm("Delete this task?")) {
                  remove.mutate(task!.id, { onSuccess: onClose });
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
